import {
  ChangeDetectionStrategy, Component, computed, DestroyRef, Directive, effect, ElementRef, forwardRef,
  inject, InjectionToken, input, output, signal, untracked,
} from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { catchError, map, of, startWith } from 'rxjs';
import { AbstractControl, FormControl, FormGroup, NgControl, ReactiveFormsModule, ValidationErrors, ValidatorFn } from '@angular/forms';
import { requiredTrimmed } from '../../../shared/validators/required-trimmed.validator';
import { LocalizationCountry, LocalizationService } from '../../../core/services/localization.service';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { ToastService } from '../../../ui';
import {
  PhoneNumberType,
  AddressCheckComponents,
  RegulationAddressDetails,
  RegulationAddressRequirements,
  RegulationIdentityRequirements,
  RegulationFormValue,
  RegulationInformation,
  Zipcode,
} from '../cc-numbers.model';
import {
  HlmBtnDirective,
  HlmIconComponent,
  HlmInputDirective,
  HlmSelectComponent,
  HlmSpinnerComponent,
  HlmTooltipDirective,
  HLM_FORM_FIELD,
} from '../../../ui';

const MAX_ZIP_RESULTS = 50;
const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10 MB
const ALLOWED_FILE_TYPES = ['application/pdf', 'image/jpeg', 'image/png'];

/** What `RevealInvalidDirective` reads from its host form. */
interface RevealContext {
  revealErrors(): boolean;
}
const REVEAL_CONTEXT = new InjectionToken<RevealContext>('RegulationFormRevealContext');

/**
 * Submit-to-reveal marking for the regulation form's inputs (`cmtRevealInvalid` on each
 * `input[formControlName]`): sets `aria-invalid="true"`, which hlmInput renders as the red
 * border (`aria-[invalid=true]:border-destructive`) — written once instead of per input. Active only after the host tried to submit
 * (`revealErrors()`); `cmtRevealMissing` adds a condition the control's own validity does not
 * carry (a typed-but-unpicked ZIP, a nationality demanded by an ID proof). `data-reveal-missing`
 * is the scroll/focus target of `revealMissing()`.
 * @ticket EB-3809
 */
@Directive({
  selector: 'input[formControlName][cmtRevealInvalid]',
  standalone: true,
  host: {
    // The red border comes from hlmInput's `aria-[invalid=true]:` variant — a class binding
    // would lose to `border-input` in the CSS order (see ui/input.directive.ts).
    '[attr.aria-invalid]': 'missing() ? "true" : null',
    '[attr.data-reveal-missing]': 'missing() ? "" : null',
  },
})
export class RevealInvalidDirective {
  private readonly context = inject(REVEAL_CONTEXT);
  private readonly control = inject(NgControl, { self: true });
  readonly cmtRevealMissing = input(false);

  missing(): boolean {
    return this.context.revealErrors() && (!!this.control.invalid || this.cmtRevealMissing());
  }
}

/** PS-6489: the address portion of the form, as consumed by the geocode address check. */
export interface RegulationAddressSnapshot {
  streetName: string;
  buildingNumber: string;
  buildingLetter: string;
  zipCode: string;
  city: string;
}

/**
 * Shared, requirements-driven regulation form (address + identity fields + the two proof-file
 * drop zones). Extracted from `book-number-dialog` so booking and the fix-regulation dialog use one
 * implementation. The host drives which fields show / are mandatory via [requirements]; [prefill]
 * pre-populates the controls (fix flow). The host pulls the validated payload via getValue() and
 * gates submit via isValid().
 */
@Component({
  selector: 'cmt-regulation-form',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    ReactiveFormsModule,
    MatAutocompleteModule,
    ...HLM_FORM_FIELD,
    HlmBtnDirective, HlmIconComponent, HlmInputDirective,
    HlmSelectComponent, HlmSpinnerComponent, HlmTooltipDirective,
    RevealInvalidDirective,
  ],
  providers: [{ provide: REVEAL_CONTEXT, useExisting: forwardRef(() => RegulationFormComponent) }],
  template: `
    <form [formGroup]="form" class="flex flex-col gap-3">

      @if (revealErrors() && missingFieldLabels().length > 0) {
        <div class="flex items-start gap-2 rounded-lg border border-destructive/40 bg-destructive/10 px-3 py-2" role="alert"
          data-testid="regulation-missing-summary">
          <hlm-icon name="lucideCircleAlert" size="0.9rem" class="mt-0.5 flex-none text-destructive" />
          <p class="text-sm text-foreground">
            Please complete the highlighted fields: <strong>{{ missingFieldLabels().join(', ') }}</strong>
          </p>
        </div>
      }

      <!-- General Information -->
      @if (hasGeneralFields()) {
        <div class="rounded-xl border border-border">
          <div class="flex items-center gap-2 border-b border-border px-3 py-2 text-sm font-semibold text-muted-foreground">
            <hlm-icon name="lucideUser" size="0.9rem" /> General Information
          </div>
          <div class="grid grid-cols-[repeat(auto-fill,minmax(180px,1fr))] gap-x-3 gap-y-2 p-3">
            @if (hasAddrField('companyName')) {
              <hlm-form-field class="col-span-full">
                <label hlmLabel>Company Name@if (isAddrRequired('companyName')) { <span class="font-semibold text-destructive">*</span> }</label>
                <input cmtRevealInvalid hlmInput formControlName="companyName" />
              </hlm-form-field>
            }
            @if (hasAddrField('companyVat')) {
              <hlm-form-field>
                <label hlmLabel>Company VAT@if (isAddrRequired('companyVat')) { <span class="font-semibold text-destructive">*</span> }</label>
                <input cmtRevealInvalid hlmInput formControlName="companyVat" />
              </hlm-form-field>
            }
            @if (hasIdentField('salutation')) {
              <hlm-form-field>
                <label hlmLabel>Salutation@if (isIdentRequired('salutation')) { <span class="font-semibold text-destructive">*</span> }</label>
                <hlm-select
                  [class]="selectMissing(form.controls.salutation) ? 'border-destructive' : ''"
                  [attr.data-reveal-missing]="selectMissing(form.controls.salutation) ? '' : null"
                  [options]="[{value:'Mr',label:'Mr.'},{value:'Mrs',label:'Mrs.'},{value:'Ms',label:'Ms.'}]"
                  [value]="form.controls.salutation.value"
                  (valueChange)="form.controls.salutation.setValue($any($event) || '')"
                />
              </hlm-form-field>
            }
            @if (hasIdentField('firstName')) {
              <hlm-form-field>
                <label hlmLabel>First Name@if (isIdentRequired('firstName')) { <span class="font-semibold text-destructive">*</span> }</label>
                <input cmtRevealInvalid hlmInput formControlName="firstName" />
              </hlm-form-field>
            }
            @if (hasIdentField('lastName')) {
              <hlm-form-field>
                <label hlmLabel>Last Name@if (isIdentRequired('lastName')) { <span class="font-semibold text-destructive">*</span> }</label>
                <input cmtRevealInvalid hlmInput formControlName="lastName" />
              </hlm-form-field>
            }
            @if (hasIdentField('dateOfBirth')) {
              <hlm-form-field>
                <label hlmLabel>Date of Birth@if (isIdentRequired('dateOfBirth')) { <span class="font-semibold text-destructive">*</span> }</label>
                <input cmtRevealInvalid hlmInput type="date" formControlName="dateOfBirth" />
              </hlm-form-field>
            }
            @if (hasIdentField('placeOfBirth')) {
              <hlm-form-field>
                <label hlmLabel>Place of Birth@if (isIdentRequired('placeOfBirth')) { <span class="font-semibold text-destructive">*</span> }</label>
                <input cmtRevealInvalid hlmInput formControlName="placeOfBirth" />
              </hlm-form-field>
            }
            @if (hasIdentField('taxCode')) {
              <hlm-form-field>
                <label hlmLabel>Tax Code@if (isIdentRequired('taxCode')) { <span class="font-semibold text-destructive">*</span> }</label>
                <input cmtRevealInvalid hlmInput formControlName="taxCode" />
              </hlm-form-field>
            }
          </div>
        </div>
      }

      <!-- Address Information -->
      @if (hasAddressFields()) {
        <div class="rounded-xl border border-border">
          <div class="flex items-center gap-2 border-b border-border px-3 py-2 text-sm font-semibold text-muted-foreground">
            <hlm-icon name="lucideMapPin" size="0.9rem" /> Address Information
          </div>
          <div class="flex flex-col gap-2 p-3">
            <!-- Row 1: Street (wide) + Building Nr (small) + Building Letter (small) -->
            <div class="flex flex-wrap gap-3">
              @if (hasAddrField('streetName')) {
                <hlm-form-field class="min-w-0 flex-[3_1_140px]">
                  <label hlmLabel>Street@if (isAddrRequired('streetName')) { <span class="font-semibold text-destructive">*</span> }</label>
                  <input cmtRevealInvalid hlmInput formControlName="streetName" />
                </hlm-form-field>
              }
              @if (hasAddrField('buildingNumber')) {
                <hlm-form-field class="max-w-[135px] flex-[0_1_135px]">
                  <label hlmLabel>Building Nr@if (isAddrRequired('buildingNumber')) { <span class="font-semibold text-destructive">*</span> }</label>
                  <input cmtRevealInvalid hlmInput formControlName="buildingNumber" />
                </hlm-form-field>
              }
              @if (hasAddrField('buildingLetter')) {
                <hlm-form-field class="max-w-[135px] flex-[0_1_135px]">
                  <label hlmLabel>Building Letter@if (isAddrRequired('buildingLetter')) { <span class="font-semibold text-destructive">*</span> }</label>
                  <input cmtRevealInvalid hlmInput formControlName="buildingLetter" />
                </hlm-form-field>
              }
            </div>
            <!-- Row 2: Zip + City + Province/State + Country -->
            <div class="flex flex-wrap gap-3">
              @if (hasAddrField('zipCode')) {
                <div class="min-w-0 flex-1 basis-[100px]">
                  <hlm-form-field>
                    <label hlmLabel>Zip Code@if (isAddrRequired('zipCode')) { <span class="font-semibold text-destructive">*</span> }</label>
                    @if (loadingZipcodes()) {
                      <div class="flex items-center gap-2 py-1 text-sm text-muted-foreground">
                        <hlm-spinner size="14" /> Loading...
                      </div>
                    } @else if (zipcodes().length > 0) {
                      <input cmtRevealInvalid hlmInput
                        formControlName="zipSearch"
                        [cmtRevealMissing]="form.controls.regZipCode.invalid"
                        [matAutocomplete]="zipAuto"
                        placeholder="ZIP or city..."
                        (input)="onZipSearch()" />
                      <mat-autocomplete #zipAuto="matAutocomplete"
                        (optionSelected)="onZipSelected($event)"
                        [displayWith]="displayZip">
                        @for (z of filteredZipcodes(); track z.zipCode) {
                          <mat-option [value]="z">
                            <span class="flex items-center gap-2">
                              <span class="font-semibold text-primary">{{ z.zipCode }}</span>
                              @if (z.cityName) {
                                <span class="text-muted-foreground">|</span>
                                <span>{{ z.cityName }}</span>
                              }
                            </span>
                          </mat-option>
                        }
                        @if (filteredZipcodes().length === 0 && zipSearchValue()) {
                          <mat-option disabled>No match</mat-option>
                        }
                      </mat-autocomplete>
                    } @else {
                      <input cmtRevealInvalid hlmInput formControlName="regZipCode" placeholder="Enter ZIP" />
                    }
                  </hlm-form-field>
                </div>
              }
              @if (hasAddrField('city')) {
                <hlm-form-field class="min-w-0 flex-1 basis-[100px]">
                  <label hlmLabel>City@if (isAddrRequired('city')) { <span class="font-semibold text-destructive">*</span> }</label>
                  <input cmtRevealInvalid hlmInput formControlName="regCity" [readonly]="isFixedValue('city')"
                    [class]="isFixedValue('city') ? 'bg-primary/5 text-muted-foreground' : ''" />
                </hlm-form-field>
              }
              @if (hasAddrField('province')) {
                <hlm-form-field class="min-w-0 flex-1 basis-[100px]">
                  <label hlmLabel>Province@if (isAddrRequired('province')) { <span class="font-semibold text-destructive">*</span> }</label>
                  <input cmtRevealInvalid hlmInput formControlName="province" />
                </hlm-form-field>
              }
              @if (hasAddrField('state')) {
                <hlm-form-field class="min-w-0 flex-1 basis-[100px]">
                  <label hlmLabel>State@if (isAddrRequired('state')) { <span class="font-semibold text-destructive">*</span> }</label>
                  <input cmtRevealInvalid hlmInput formControlName="state" />
                </hlm-form-field>
              }
              @if (hasAddrField('countryCodeA3')) {
                <hlm-form-field class="min-w-0 flex-1 basis-[100px]">
                  <label hlmLabel>Country@if (isAddrRequired('countryCodeA3')) { <span class="font-semibold text-destructive">*</span> }</label>
                  @if (isFixedValue('countryCodeA3')) {
                    <input hlmInput [value]="fixedCountryName() ?? form.controls.countryCodeA3.value" readonly class="bg-primary/5 text-muted-foreground" />
                  } @else {
                    <input cmtRevealInvalid hlmInput formControlName="countryCodeA3" />
                  }
                </hlm-form-field>
              }
            </div>
            @if (hasAddrField('proofDocument')) {
              <div class="col-span-full flex cursor-pointer items-center gap-2 rounded-lg border-2 border-dashed px-3 py-2 transition-colors hover:border-primary/30 hover:bg-primary/5"
                [class]="addressProofFile() ? 'border-solid border-success bg-success/5'
                  : (addressProofMissing() ? 'border-destructive bg-destructive/5' : 'border-border bg-muted/30')"
                role="button" tabindex="0" aria-label="Upload address proof"
                [attr.aria-invalid]="addressProofMissing() ? 'true' : null"
                [attr.data-reveal-missing]="addressProofMissing() ? '' : null"
                (keydown.enter)="$event.preventDefault(); addrFileInput.click()"
                (keydown.space)="$event.preventDefault(); addrFileInput.click()"
                (click)="addrFileInput.click()">
                <input #addrFileInput type="file" hidden accept=".pdf,.jpg,.jpeg,.png" (change)="onAddressFileSelected($event)" />
                @if (addressProofFile()) {
                  <hlm-icon name="lucideFileText" size="1rem" class="text-success" />
                  <div class="flex min-w-0 flex-1 flex-col">
                    <span class="truncate text-sm font-medium text-foreground">{{ addressProofFile()!.name }}</span>
                    <span class="text-sm text-muted-foreground">{{ formatFileSize(addressProofFile()!.size) }}</span>
                  </div>
                  <button hlmBtn variant="ghost" size="icon" class="h-7 w-7" (click)="clearAddressFile($event)" hlmTooltip="Remove">
                    <hlm-icon name="lucideX" size="0.8rem" />
                  </button>
                } @else {
                  <hlm-icon name="lucideUploadCloud" size="1rem" class="text-muted-foreground" />
                  <div class="flex flex-col">
                    <span class="text-sm font-medium text-foreground">Address proof <span class="font-semibold text-destructive">*</span></span>
                    <span class="text-sm text-muted-foreground">PDF, JPG, PNG</span>
                  </div>
                }
              </div>
            }

            <!-- PS-6489: host slot for the geocode address check, rendered by the booking dialog.
                 Kept as projection so this shared form stays free of booking-specific dependencies. -->
            <ng-content select="[addressCheck]" />
          </div>
        </div>
      }

      <!-- Identity Information -->
      @if (hasIdentityFields()) {
        <div class="rounded-xl border border-border">
          <div class="flex items-center gap-2 border-b border-border px-3 py-2 text-sm font-semibold text-muted-foreground">
            <hlm-icon name="lucideShield" size="0.9rem" /> Identity Information
          </div>
          <div class="grid grid-cols-[repeat(auto-fill,minmax(180px,1fr))] gap-x-3 gap-y-2 p-3">
            @if (hasIdentField('nationality') || nationalityNeededForProof()) {
              <hlm-form-field>
                <label hlmLabel>Nationality@if (isIdentRequired('nationality') || nationalityNeededForProof()) { <span class="font-semibold text-destructive">*</span> }</label>
                <input cmtRevealInvalid hlmInput
                  formControlName="nationalitySearch"
                  [cmtRevealMissing]="nationalityNeededForProof() && !form.controls.nationalitySearch.value"
                  [matAutocomplete]="natAuto"
                  placeholder="Search..."
                  (input)="onNationalitySearch()" />
                <mat-autocomplete #natAuto="matAutocomplete"
                  (optionSelected)="onNationalitySelected()">
                  @for (c of filteredNationalities(); track c.isoCountryCode) {
                    <mat-option [value]="c.name">{{ c.name }}</mat-option>
                  }
                  @if (filteredNationalities().length === 0 && nationalitySearchValue()) {
                    <mat-option disabled>No match</mat-option>
                  }
                </mat-autocomplete>
                @if (form.controls.nationalitySearch.hasError('notInList') && (revealErrors() || form.controls.nationalitySearch.touched)) {
                  <p hlmFieldHint class="text-destructive" data-testid="nationality-not-in-list">Pick a nationality from the list.</p>
                }
                @if (nationalityNeededForProof()) {
                  <p hlmFieldHint data-testid="nationality-needed-for-proof"
                    [class.text-destructive]="revealErrors() && !form.controls.nationalitySearch.value">
                    Required so the ID proof is transmitted — without a nationality no identity data is sent.
                  </p>
                }
              </hlm-form-field>
            }
            @if (hasIdentField('identityDocumentType')) {
              <hlm-form-field>
                <label hlmLabel>Document Type@if (isIdentRequired('identityDocumentType')) { <span class="font-semibold text-destructive">*</span> }</label>
                <hlm-select
                  [class]="selectMissing(form.controls.identityDocumentType) ? 'border-destructive' : ''"
                  [attr.data-reveal-missing]="selectMissing(form.controls.identityDocumentType) ? '' : null"
                  [options]="[{value:'PASSPORT',label:'Passport'},{value:'NATIONAL_ID_CARD',label:'National ID Card'},{value:'BUSINESS_REGISTRATION',label:'Business Registration'}]"
                  [value]="form.controls.identityDocumentType.value"
                  (valueChange)="form.controls.identityDocumentType.setValue($any($event) || '')"
                />
              </hlm-form-field>
            }
            @if (hasIdentField('identityDocumentNumber')) {
              <hlm-form-field>
                <label hlmLabel>Document Number@if (isIdentRequired('identityDocumentNumber')) { <span class="font-semibold text-destructive">*</span> }</label>
                <input cmtRevealInvalid hlmInput formControlName="identityDocumentNumber" />
              </hlm-form-field>
            }
            @if (hasIdentField('issuingAuthority')) {
              <hlm-form-field>
                <label hlmLabel>Issuing Authority@if (isIdentRequired('issuingAuthority')) { <span class="font-semibold text-destructive">*</span> }</label>
                <input cmtRevealInvalid hlmInput formControlName="issuingAuthority" />
              </hlm-form-field>
            }
            @if (hasIdentField('issuingDate')) {
              <hlm-form-field>
                <label hlmLabel>Issuing Date@if (isIdentRequired('issuingDate')) { <span class="font-semibold text-destructive">*</span> }</label>
                <input cmtRevealInvalid hlmInput type="date" formControlName="issuingDate" />
              </hlm-form-field>
            }
            @if (hasIdentField('proofDocument')) {
              <div class="col-span-full flex cursor-pointer items-center gap-2 rounded-lg border-2 border-dashed px-3 py-2 transition-colors hover:border-primary/30 hover:bg-primary/5"
                [class]="identityProofFile() ? 'border-solid border-success bg-success/5'
                  : (identityProofMissing() ? 'border-destructive bg-destructive/5' : 'border-border bg-muted/30')"
                role="button" tabindex="0" aria-label="Upload identity proof"
                [attr.aria-invalid]="identityProofMissing() ? 'true' : null"
                [attr.data-reveal-missing]="identityProofMissing() ? '' : null"
                (keydown.enter)="$event.preventDefault(); idFileInput.click()"
                (keydown.space)="$event.preventDefault(); idFileInput.click()"
                (click)="idFileInput.click()">
                <input #idFileInput type="file" hidden accept=".pdf,.jpg,.jpeg,.png" (change)="onIdentityFileSelected($event)" />
                @if (identityProofFile()) {
                  <hlm-icon name="lucideFileText" size="1rem" class="text-success" />
                  <div class="flex min-w-0 flex-1 flex-col">
                    <span class="truncate text-sm font-medium text-foreground">{{ identityProofFile()!.name }}</span>
                    <span class="text-sm text-muted-foreground">{{ formatFileSize(identityProofFile()!.size) }}</span>
                  </div>
                  <button hlmBtn variant="ghost" size="icon" class="h-7 w-7" (click)="clearIdentityFile($event)" hlmTooltip="Remove">
                    <hlm-icon name="lucideX" size="0.8rem" />
                  </button>
                } @else {
                  <hlm-icon name="lucideUploadCloud" size="1rem" class="text-muted-foreground" />
                  <div class="flex flex-col">
                    <span class="text-sm font-medium text-foreground">Identity proof <span class="font-semibold text-destructive">*</span></span>
                    <span class="text-sm text-muted-foreground">PDF, JPG, PNG</span>
                  </div>
                }
              </div>
            }
          </div>
        </div>
      }
    </form>
  `,
})
export class RegulationFormComponent {
  private readonly toast = inject(ToastService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly host = inject<ElementRef<HTMLElement>>(ElementRef);

  // --- Inputs ---

  /** Field requirements (addressRequirements/identityRequirements/fixedValues) driving which fields show / are mandatory. */
  readonly requirements = input.required<RegulationInformation | null>();
  /** Values to pre-populate the form (fix flow); null for booking. */
  readonly prefill = input<RegulationAddressDetails | null>(null);
  /** Number type carried through into the produced payload. */
  readonly numberType = input.required<PhoneNumberType>();
  /** Zip suggestions for the zip autocomplete (booking loads these by country/city; null/empty → manual ZIP input). */
  readonly zipcodes = input<Zipcode[]>([]);
  /** Whether the zip suggestions are still loading (booking flow). */
  readonly loadingZipcodes = input(false);
  /** Display name for a fixed country (booking shows the selected country's name in the read-only field). */
  readonly fixedCountryName = input<string | null>(null);
  /**
   * Nationality suggestions — the full localization country list, as old CMT number.dialog
   * `setBrandIdAndCountries()` (`LocalizationService.getCountries()`). A nationality is independent
   * of the number inventory: feeding the booking dialog's bookable countries (FAX/CALLCENTER)
   * hid most nationalities, and the fix-regulation dialog passed none at all (EB-3809 PR review).
   * @ticket EB-3809
   */
  readonly nationalityOptions = toSignal(
    inject(LocalizationService).getCountries().pipe(
      map(list => list
        // Real data carries a nameless entry (id 207, ASM) — unpickable, and it broke the sort.
        .filter((c): c is LocalizationCountry & { name: string } => !!c.name)
        .map(c => ({ isoCountryCode: c.countryCode2, name: c.name }))
        .sort((a, b) => a.name.localeCompare(b.name))),
      // A toSignal source that errors rethrows on every read and takes the whole form down —
      // no list is better than no form (the pick-from-list check then stands down).
      catchError(() => of([] as { isoCountryCode: string; name: string }[])),
    ),
    { initialValue: [] as { isoCountryCode: string; name: string }[] },
  );

  /** PS-6489: emits the address portion on every change so the booking dialog can run the
   *  geocode check. Emitted for any edit — debouncing and dedupe are the consumer's job. */
  readonly addressValueChange = output<RegulationAddressSnapshot>();

  // --- Proof files ---

  readonly addressProofFile = signal<File | null>(null);
  readonly identityProofFile = signal<File | null>(null);

  // --- Search state ---

  readonly zipSearchValue = signal('');
  readonly nationalitySearchValue = signal('');

  // --- Form (regulation subset only — booking-only country/city/zip/type live in the host) ---

  readonly form = new FormGroup({
    // General
    companyName: new FormControl('', { nonNullable: true }),
    companyVat: new FormControl('', { nonNullable: true }),
    salutation: new FormControl('', { nonNullable: true }),
    firstName: new FormControl('', { nonNullable: true }),
    lastName: new FormControl('', { nonNullable: true }),
    dateOfBirth: new FormControl('', { nonNullable: true }),
    placeOfBirth: new FormControl('', { nonNullable: true }),
    taxCode: new FormControl('', { nonNullable: true }),
    // Address
    streetName: new FormControl('', { nonNullable: true }),
    buildingNumber: new FormControl('', { nonNullable: true }),
    buildingLetter: new FormControl('', { nonNullable: true }),
    zipSearch: new FormControl('', { nonNullable: true }),
    regZipCode: new FormControl('', { nonNullable: true }),
    regCity: new FormControl('', { nonNullable: true }),
    province: new FormControl('', { nonNullable: true }),
    state: new FormControl('', { nonNullable: true }),
    countryCodeA3: new FormControl('', { nonNullable: true }),
    // Identity
    nationalitySearch: new FormControl('', { nonNullable: true }),
    identityDocumentType: new FormControl('', { nonNullable: true }),
    identityDocumentNumber: new FormControl('', { nonNullable: true }),
    issuingAuthority: new FormControl('', { nonNullable: true }),
    issuingDate: new FormControl('', { nonNullable: true }),
  });

  constructor() {
    // PS-6489: publish the address on every edit. startWith so a prefilled form (fix-regulation
    // flow) reaches the consumer without requiring a keystroke first.
    this.form.valueChanges
      .pipe(startWith(null), takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.addressValueChange.emit(this.addressSnapshot()));

    // React to requirements changes (e.g. booking switches city): reset to a clean slate, pre-fill
    // fixed values, re-apply validators, then apply any prefill (fix flow).
    // Only `requirements` may re-run this: the body sets values and validators, which run validators
    // that read other signals (e.g. `nationalityOptions`). Tracked, the country list arriving after
    // the operator started typing wiped the whole form (EB-3809 review run 3).
    effect(() => {
      const requirements = this.requirements(); // track
      untracked(() => {
        this.resetControls();
        const fixed = requirements?.fixedValues;
        if (fixed) {
          if (fixed.city) this.form.controls.regCity.setValue(fixed.city);
          if (fixed.countryCodeA3) this.form.controls.countryCodeA3.setValue(fixed.countryCodeA3);
        }
        this.updateRegulationValidators();
        this.applyPrefill();
      });
    });

    // Keep the ZIP-required toggle in sync when suggestions load/clear (booking switches from the
    // manual input to the autocomplete). This re-applies validators only — it must NOT reset the
    // form, so it lives in its own effect and the main reset effect reads `zipcodes()` untracked.
    effect(() => {
      this.zipcodes(); // track
      untracked(() => this.updateRegulationValidators());
    });

    // Re-judge the nationality once the country list arrives, or when the field appears because an
    // ID proof now demands it (the validator skips a hidden field).
    effect(() => {
      this.nationalityOptions(); // track
      this.nationalityNeededForProof(); // track
      untracked(() => this.form.controls.nationalitySearch.updateValueAndValidity({ emitEvent: false }));
    });
  }

  /** Reset all regulation controls + proof files + search state (mirrors the old clearRegulationState). */
  private resetControls(): void {
    this.revealErrors.set(false);
    this.addressProofFile.set(null);
    this.identityProofFile.set(null);
    this.nationalitySearchValue.set('');
    this.zipSearchValue.set('');
    for (const key of Object.keys(this.form.controls) as (keyof typeof this.form.controls)[]) {
      const ctrl = this.form.controls[key];
      ctrl.reset('', { emitEvent: false });
    }
  }

  // --- Prefill ---

  private applyPrefill(): void {
    const p = this.prefill();
    if (!p) return;
    const f = this.form.controls;
    const set = (ctrl: FormControl, value: string | null) => {
      if (value != null && value !== '') ctrl.setValue(value);
    };
    set(f.companyName, p.company);
    set(f.companyVat, p.companyVat);
    set(f.streetName, p.street);
    set(f.buildingNumber, p.buildingNumber);
    set(f.buildingLetter, p.buildingLetter);
    set(f.regZipCode, p.zipCode);
    set(f.regCity, p.city);
    set(f.province, p.province);
    set(f.state, p.state);
    set(f.countryCodeA3, p.countryCode);
    set(f.salutation, p.salutation);
    set(f.firstName, p.firstName);
    set(f.lastName, p.lastName);
    set(f.nationalitySearch, p.nationality);
    set(f.identityDocumentType, p.idType);
    set(f.identityDocumentNumber, p.idNumber);
    set(f.dateOfBirth, p.dateOfBirth);
    set(f.placeOfBirth, p.placeOfBirth);
    set(f.taxCode, p.taxCode);
    set(f.issuingAuthority, p.issuingAuthority);
    set(f.issuingDate, p.issuingDate);
  }

  // --- Zip autocomplete ---

  readonly filteredZipcodes = computed(() => {
    const q = this.zipSearchValue().toLowerCase();
    const all = this.zipcodes();
    if (!q) return all.slice(0, MAX_ZIP_RESULTS);
    return all.filter(z =>
      z.zipCode.toLowerCase().includes(q) || (z.cityName?.toLowerCase().includes(q) ?? false),
    ).slice(0, MAX_ZIP_RESULTS);
  });

  displayZip = (zip: Zipcode | string): string => {
    if (!zip) return '';
    if (typeof zip === 'string') return zip;
    return zip.cityName ? `${zip.zipCode} | ${zip.cityName}` : zip.zipCode;
  };

  onZipSearch(): void {
    const val = this.form.controls.zipSearch.value;
    this.zipSearchValue.set(typeof val === 'string' ? val : '');
    if (typeof val === 'string') {
      this.form.controls.regZipCode.setValue('');
    }
  }

  onZipSelected(event: any): void {
    const zip: Zipcode = event.option.value;
    this.form.controls.regZipCode.setValue(zip.zipCode);
    this.zipSearchValue.set('');
  }

  // --- Nationality autocomplete ---

  readonly filteredNationalities = computed(() => {
    const q = this.nationalitySearchValue().toLowerCase();
    const all = this.nationalityOptions();
    if (!q) return all.slice(0, 30);
    return all.filter(c => c.name.toLowerCase().includes(q)).slice(0, 30);
  });

  onNationalitySearch(): void {
    const val = this.form.controls.nationalitySearch.value;
    this.nationalitySearchValue.set(typeof val === 'string' ? val : '');
  }

  onNationalitySelected(): void {
    this.nationalitySearchValue.set('');
  }

  // --- File upload ---

  onAddressFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files?.length) {
      const file = input.files[0];
      if (!this.validateFile(file)) { input.value = ''; return; }
      this.addressProofFile.set(file);
    }
  }

  onIdentityFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files?.length) {
      const file = input.files[0];
      if (!this.validateFile(file)) { input.value = ''; return; }
      this.identityProofFile.set(file);
    }
  }

  private validateFile(file: File): boolean {
    if (file.size > MAX_FILE_SIZE) {
      this.toast.open('File exceeds 10 MB limit', 'OK', { duration: 5000, tone: 'error' });
      return false;
    }
    if (!ALLOWED_FILE_TYPES.includes(file.type)) {
      this.toast.open('Only PDF, JPG, and PNG files are allowed', 'OK', { duration: 5000, tone: 'error' });
      return false;
    }
    return true;
  }

  clearAddressFile(event: Event): void {
    event.stopPropagation();
    this.addressProofFile.set(null);
  }

  clearIdentityFile(event: Event): void {
    event.stopPropagation();
    this.identityProofFile.set(null);
  }

  formatFileSize(bytes: number): string {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1048576) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / 1048576).toFixed(1) + ' MB';
  }

  // --- Dynamic validators ---

  private updateRegulationValidators(): void {
    const a = this.addrReq();
    const i = this.identReq();
    const f = this.form.controls;

    const setReq = (ctrl: FormControl, req?: boolean) => {
      // Whitespace-only counts as missing, as in old CMT (ng-trim) — else the trimmed address check
      // stays incomplete behind a "valid" form and Book is a silent click (EB-3809 PR review).
      ctrl.setValidators(req ? requiredTrimmed : []);
      ctrl.updateValueAndValidity({ emitEvent: false });
    };

    // Address requirements
    setReq(f.companyName, a?.companyName);
    setReq(f.companyVat, a?.companyVat);
    setReq(f.streetName, a?.streetName);
    setReq(f.buildingNumber, a?.buildingNumber);
    setReq(f.buildingLetter, a?.buildingLetter);
    // ZIP is entered via one of two mutually-exclusive controls (see template): the `zipSearch`
    // autocomplete when suggestions are loaded (booking), or the manual `regZipCode` input when
    // there are none (fix flow). Only require the control that is actually shown — otherwise the
    // fix flow, which never loads suggestions, leaves `zipSearch` empty and can never validate.
    const hasZipSuggestions = untracked(() => this.zipcodes()).length > 0;
    setReq(f.zipSearch, a?.zipCode && hasZipSuggestions);
    setReq(f.regZipCode, a?.zipCode);
    setReq(f.regCity, a?.city);
    setReq(f.province, a?.province);
    setReq(f.state, a?.state);
    setReq(f.countryCodeA3, a?.countryCodeA3);

    // Identity requirements
    setReq(f.salutation, i?.salutation);
    setReq(f.firstName, i?.firstName);
    setReq(f.lastName, i?.lastName);
    setReq(f.dateOfBirth, i?.dateOfBirth);
    setReq(f.placeOfBirth, i?.placeOfBirth);
    setReq(f.taxCode, i?.taxCode);
    setReq(f.nationalitySearch, i?.nationality);
    f.nationalitySearch.addValidators(this.nationalityInList);
    f.nationalitySearch.updateValueAndValidity({ emitEvent: false });
    setReq(f.identityDocumentType, i?.identityDocumentType);
    setReq(f.identityDocumentNumber, i?.identityDocumentNumber);
    setReq(f.issuingAuthority, i?.issuingAuthority);
    setReq(f.issuingDate, i?.issuingDate);
  }

  /**
   * A nationality must be picked from the list, as old CMT's `ui-select` (no free text): a typed
   * "Ger" used to pass and went to the carrier as-is. The value stays the country NAME, which is
   * what old CMT sends (`country.name as country in nationalities`). Skipped until the list has
   * loaded, so a slow or failed load never blocks the form on its own.
   *
   * Also skipped while the field is not rendered, and for the unchanged prefilled value (fix
   * flow): old CMT's `ui-select` keeps a stored model value that is not in the list and
   * `ng-required` passes — a stored "DEU" must not block re-submitting a rejected regulation
   * (EB-3809 review run 3). Signals are read untracked: validators run inside effects.
   * @ticket EB-3809
   */
  private readonly nationalityInList: ValidatorFn = (control: AbstractControl): ValidationErrors | null => {
    const value = typeof control.value === 'string' ? control.value : '';
    if (!value.trim()) return null;
    return untracked(() => {
      const options = this.nationalityOptions();
      if (options.length === 0) return null;
      if (!this.hasIdentField('nationality') && !this.nationalityNeededForProof()) return null;
      if (value === this.prefill()?.nationality) return null;
      // Exact match: the payload carries this string verbatim, so "germany" / " Germany" must be re-picked.
      return options.some(o => o.name === value) ? null : { notInList: true };
    });
  };

  // --- Regulation helpers ---

  readonly addrReq = computed(() => this.requirements()?.addressRequirements ?? null);
  readonly identReq = computed(() => this.requirements()?.identityRequirements ?? null);
  readonly fixedValues = computed(() => this.requirements()?.fixedValues ?? null);

  isFixedValue(field: string): boolean {
    return !!(this.fixedValues()?.[field]);
  }

  /** Check if address field exists in requirements (show even if optional=false) */
  hasAddrField(field: string): boolean {
    const req = this.addrReq();
    return req !== null && field in req;
  }

  /** Check if identity field exists in requirements (show even if optional=false) */
  hasIdentField(field: string): boolean {
    const req = this.identReq();
    return req !== null && field in req;
  }

  hasGeneralFields(): boolean {
    return this.hasAddrField('companyName') || this.hasAddrField('companyVat') ||
      this.hasIdentField('salutation') || this.hasIdentField('firstName') ||
      this.hasIdentField('lastName') || this.hasIdentField('dateOfBirth') ||
      this.hasIdentField('placeOfBirth') || this.hasIdentField('taxCode');
  }

  /** Mandatory = the requirement flag is true, which is exactly what drives requiredTrimmed
   *  in updateRegulationValidators(). Field presence alone means "shown", not "mandatory". */
  isAddrRequired(field: keyof RegulationAddressRequirements): boolean {
    return this.addrReq()?.[field] === true;
  }

  isIdentRequired(field: keyof RegulationIdentityRequirements): boolean {
    return this.identReq()?.[field] === true;
  }

  hasAddressFields(): boolean {
    return this.hasAddrField('streetName') || this.hasAddrField('buildingNumber') ||
      this.hasAddrField('buildingLetter') || this.hasAddrField('city') ||
      this.hasAddrField('zipCode') || this.hasAddrField('province') ||
      this.hasAddrField('state') || this.hasAddrField('countryCodeA3') ||
      this.hasAddrField('proofDocument');
  }

  hasIdentityFields(): boolean {
    return this.hasIdentField('identityDocumentNumber') || this.hasIdentField('identityDocumentType') ||
      this.hasIdentField('issuingAuthority') || this.hasIdentField('issuingDate') ||
      this.hasIdentField('nationality') || this.hasIdentField('proofDocument');
  }

  // --- Public API for the host ---

  /** PS-6489: adopt the geocoder's corrected address into the form.
   *
   *  Patched with `emitEvent: false` on purpose: the form's valueChanges feed the address check,
   *  so emitting here would immediately invalidate the verdict the operator just confirmed, spend
   *  another rate-limited geocode call, and ask them to confirm the same correction again.
   *  A later manual edit still emits normally and re-triggers the check, as it should.
   */
  applyAddressCorrection(components: AddressCheckComponents): void {
    const patch: Record<string, string> = {};
    if (components.street) patch['streetName'] = components.street;
    if (components.city) patch['regCity'] = components.city;
    if (components.zipCode) {
      patch['regZipCode'] = components.zipCode;
      // Keep the visible autocomplete field in step with the value that will be submitted.
      patch['zipSearch'] = components.zipCode;
    }
    if (components.buildingNumber) {
      // The geocoder returns one house-number field, the form has number + letter ("9b" -> 9 | b).
      const match = /^\s*(\d+)\s*([A-Za-z])?\s*$/.exec(components.buildingNumber);
      if (match) {
        patch['buildingNumber'] = match[1];
        patch['buildingLetter'] = match[2] ?? '';
      } else {
        patch['buildingNumber'] = components.buildingNumber;
      }
    }
    // country is a fixed, read-only value in this form — never overwritten from the geocoder.
    this.form.patchValue(patch, { emitEvent: false });
  }

  /** The ZIP the operator actually entered, from whichever of the two mutually exclusive controls
   *  currently holds it.
   *
   *  The booking flow shows a `zipSearch` autocomplete, the fix flow a manual `regZipCode` input.
   *  `onZipSearch()` clears `regZipCode` on every keystroke and only `onZipSelected()` re-sets it,
   *  so a ZIP that was typed but never picked from the suggestion list lives ONLY in `zipSearch`.
   *  Reading `regZipCode` alone therefore loses it, which left the address check stuck on
   *  "pending" with no call and no explanation (found in the live test, 2026-08-24).
   *  The booking payload was never actually exposed — `regZipCode` carries requiredTrimmed,
   *  so submit is blocked until a suggestion is picked — but it reads the ZIP through here too,
   *  so the two can no longer disagree about what the operator entered.
   */
  private effectiveZipCode(raw: { regZipCode?: string; zipSearch?: unknown }): string {
    const picked = (raw.regZipCode ?? '').trim();
    if (picked) return picked;

    const search = raw.zipSearch;
    // mat-autocomplete puts the whole Zipcode object into the control once an option is chosen.
    if (search && typeof search === 'object' && 'zipCode' in search) {
      return String((search as Zipcode).zipCode ?? '').trim();
    }
    // Freely typed: accept only a complete postal code, so no call fires on half-typed input.
    const typed = typeof search === 'string' ? /\b\d{4,5}\b/.exec(search) : null;
    return typed ? typed[0] : '';
  }

  /** Current address values, trimmed of the form-control naming quirks (regZipCode/regCity). */
  addressSnapshot(): RegulationAddressSnapshot {
    const v = this.form.getRawValue();
    return {
      streetName: v.streetName ?? '',
      buildingNumber: v.buildingNumber ?? '',
      buildingLetter: v.buildingLetter ?? '',
      zipCode: this.effectiveZipCode(v),
      city: v.regCity ?? '',
    };
  }

  /**
   * The payload drops the whole identity block without a nationality (old CMT parity, see
   * `buildRegulationPayload`), and phoneservice-api attaches the ID proof file only to a non-null
   * identity block. So whenever an ID proof is required or chosen, a nationality is demanded too —
   * shown as a mandatory field with a hint, even if the city's requirements do not list it.
   *
   * Defensive only: phoneservice-api's `IdentityRequirements.nationality` is hard-wired to `true`
   * (no setter, no builder method), so today nationality is always mandatory whenever an identity
   * block exists and this never fires. It guards the payload rule should that flag ever become
   * configurable (EB-3809 PR review).
   * @ticket EB-3809
   */
  readonly nationalityNeededForProof = computed(() =>
    !this.isIdentRequired('nationality')
    && (this.identReq()?.proofDocument === true || this.identityProofFile() !== null));

  /** Set by `revealMissing()` (host submit on an incomplete form): outline every missing field. */
  readonly revealErrors = signal(false);

  /**
   * Labels (as rendered) of every mandatory input that is still empty — drives the host's footer
   * hint and the in-form summary. Order follows the form top to bottom.
   * @ticket EB-3809
   */
  missingFieldLabels(): string[] {
    const f = this.form.controls;
    const missing: string[] = [];
    const check = (ctrl: FormControl, label: string) => {
      if (ctrl.hasError('required') && !missing.includes(label)) missing.push(label);
    };
    check(f.companyName, 'Company Name');
    check(f.companyVat, 'Company VAT');
    check(f.salutation, 'Salutation');
    check(f.firstName, 'First Name');
    check(f.lastName, 'Last Name');
    check(f.dateOfBirth, 'Date of Birth');
    check(f.placeOfBirth, 'Place of Birth');
    check(f.taxCode, 'Tax Code');
    check(f.streetName, 'Street');
    check(f.buildingNumber, 'Building Nr');
    check(f.buildingLetter, 'Building Letter');
    check(f.zipSearch, 'Zip Code');
    check(f.regZipCode, 'Zip Code');
    check(f.regCity, 'City');
    check(f.province, 'Province');
    check(f.state, 'State');
    check(f.countryCodeA3, 'Country');
    if (this.addrReq()?.proofDocument === true && !this.addressProofFile()) missing.push('Address proof');
    check(f.nationalitySearch, 'Nationality');
    if (f.nationalitySearch.hasError('notInList') && !missing.includes('Nationality')) missing.push('Nationality');
    if (this.nationalityNeededForProof() && !f.nationalitySearch.value && !missing.includes('Nationality')) {
      missing.push('Nationality');
    }
    check(f.identityDocumentType, 'Document Type');
    check(f.identityDocumentNumber, 'Document Number');
    check(f.issuingAuthority, 'Issuing Authority');
    check(f.issuingDate, 'Issuing Date');
    if (this.identReq()?.proofDocument === true && !this.identityProofFile()) missing.push('Identity proof');
    return missing;
  }

  /**
   * Submit-to-reveal: the host's Book/Submit button stays clickable on an incomplete form; a
   * click calls this instead of submitting — outlines every missing field, shows the summary and
   * scrolls the first missing field into view.
   * @ticket EB-3809
   */
  revealMissing(): void {
    this.revealErrors.set(true);
    this.form.markAllAsTouched();
    // After the next render, when the missing markers (`data-reveal-missing`) are in the DOM.
    setTimeout(() => {
      const first = this.host.nativeElement.querySelector<HTMLElement>('[data-reveal-missing]');
      if (!first) return;
      first.scrollIntoView?.({ block: 'center', behavior: 'smooth' });
      // Inputs and drop zones take focus themselves; a select's focusable part is its trigger.
      const focusTarget = first.tagName === 'HLM-SELECT' ? first.querySelector<HTMLElement>('button') : first;
      focusTarget?.focus({ preventScroll: true });
    });
  }

  /** Submit-to-reveal marking for a select (they carry no formControlName → no directive). */
  selectMissing(ctrl: FormControl): boolean {
    return this.revealErrors() && ctrl.invalid;
  }

  addressProofMissing(): boolean {
    return this.revealErrors() && this.addrReq()?.proofDocument === true && !this.addressProofFile();
  }

  identityProofMissing(): boolean {
    return this.revealErrors() && this.identReq()?.proofDocument === true && !this.identityProofFile();
  }

  /** True when all mandatory fields (incl. required proof files) are satisfied. */
  isValid(): boolean {
    if (!this.form.valid) return false;
    if (this.nationalityNeededForProof() && !this.form.controls.nationalitySearch.value) return false;
    const reg = this.requirements();
    if (reg?.addressRequirements?.proofDocument && !this.addressProofFile()) return false;
    if (reg?.identityRequirements?.proofDocument && !this.identityProofFile()) return false;
    return true;
  }

  /** Pull the validated payload; null while invalid. */
  getValue(): RegulationFormValue | null {
    if (!this.isValid()) return null;
    return {
      regulationInformation: this.buildRegulationPayload(this.form.getRawValue()),
      numberType: this.numberType(),
      addressProofFile: this.addressProofFile(),
      identityProofFile: this.identityProofFile(),
    };
  }

  private buildRegulationPayload(val: any): Record<string, any> {
    const result: Record<string, any> = {};

    // Address fields
    const addressFields: Record<string, string> = {
      companyName: 'companyName',
      companyVat: 'companyVat',
      streetName: 'streetName',
      buildingNumber: 'buildingNumber',
      buildingLetter: 'buildingLetter',
      /**
       * Backend DTO field is `cityName` (phoneservice-api RegulationInformation, no alias; unknown
       * keys are ignored) — `city` was silently dropped. Old CMT sends regData.cityName.
       * @ticket EB-3809
       */
      regCity: 'cityName',
      province: 'province',
      state: 'state',
      countryCodeA3: 'isoCountryCode',
    };
    for (const [formKey, apiKey] of Object.entries(addressFields)) {
      if (val[formKey]) result[apiKey] = val[formKey];
    }

    // ZIP lives in one of two controls — see effectiveZipCode().
    const zipCode = this.effectiveZipCode(val);
    if (zipCode) result['zipCode'] = zipCode;

    // Identity fields (nested under identityDocumentProof)
    const identFields: Record<string, string> = {
      salutation: 'salutation',
      firstName: 'firstName',
      lastName: 'lastName',
      dateOfBirth: 'dateOfBirth',
      placeOfBirth: 'placeOfBirth',
      taxCode: 'taxCode',
      identityDocumentNumber: 'identityDocumentNumber',
      identityDocumentType: 'identityDocumentType',
      issuingAuthority: 'issuingAuthority',
      issuingDate: 'issuingDate',
      nationalitySearch: 'nationality',
    };
    const identProof: Record<string, string> = {};
    for (const [formKey, apiKey] of Object.entries(identFields)) {
      if (val[formKey]) identProof[apiKey] = val[formKey];
    }
    /**
     * Old CMT number.dialog prepareRegulationInformation: without a nationality the whole identity
     * block is sent as null, even when other identity fields are filled. `isValid()` demands a
     * nationality whenever an ID proof is involved, so the proof file is never lost this way.
     * @ticket EB-3809
     */
    result.identityDocumentProof = identProof['nationality'] ? identProof : null;

    return result;
  }
}
